import {
	ExemptedStudent,
	packageLanguageChanger,
	ThemeWrapper,
	ThirdPartyPlatform,
	ThirdPartyPlatformRosters,
	VerityInstituteSettings,
} from "@verity/components";
import type { FlaggedKeyword } from "@verity/database/prisma";
import { getInferredBrowserExtensionId } from "@verity/extension/platform";
import { dispatchProxyMessage } from "@verity/extension/proxy-message/external";
import { isInstituteProfile } from "@verity/proctoring-option";
import {
	GeofenceLocation,
	OptionRelatedInstituteSettings,
	ProctoringOption,
	ProctoringOptionProfileV2,
} from "@verity/proctoring-option/types";
import { useContext, useMemo } from "react";
import apiClient from "../../helpers/api-client";
import { canAccessResource } from "../../helpers/privilege";
import { getProduct } from "../../helpers/url";
import i18n from "../../i18n";
import BankSvg from "../../images/settings/bank.svg?react";
import BarricadeSvg from "../../images/settings/barricade.svg?react";
import BellSvg from "../../images/settings/bell.svg?react";
import SavedGeofence from "../../images/settings/enable-geofence.svg?react";
import ExemptionSvg from "../../images/settings/exemption-icon.svg?react";
import FlagSvg from "../../images/settings/flag.svg?react";
import IconBrowsers from "../../images/settings/icon-browsers.svg?react";
import LaptopSvg from "../../images/settings/laptop.svg?react";
import MethodScopeSettingSvg from "../../images/settings/method-scope-settings.svg?react";
import IncognitoSvg from "../../images/settings/no-incognito.svg?react";
import PasswordLockSvg from "../../images/settings/password-lock.svg?react";
import PenNibSvg from "../../images/settings/pen-nib.svg?react";
import PersonSpeakingSvg from "../../images/settings/person-speaking.svg?react";
import RecordingMaxAgeIcon from "../../images/settings/recording-max-age.svg?react";
import ScopeSettingSvg from "../../images/settings/scope-setting-icon.svg?react";
import SealQuestionSvg from "../../images/settings/seal-question.svg?react";
import ShieldSvg from "../../images/settings/shield-check.svg?react";
import SuffixSvg from "../../images/settings/suffix.svg?react";
import { ScopeSettingsInfo } from "../../types/general-types";
import SettingsPage, {
	CustomSetting,
	CustomSettingData,
	SaveResponse,
	SettingsPageProps,
	VeritySetting,
	VeritySettingGroup,
} from "../components/settings-page";
import {
	AllowQuizAttemptOnBrowserSetting,
	AllowQuizAttemptOnBrowserSettingData,
	allowQuizAttemptOnBrowserSettingSchema,
} from "../components/settings/allow-quiz-attempt-on-browser";
import { BypassModeInput, saveBypassMode } from "../components/settings/bypass-mode";
import {
	ChromeOsRequireManagedGuestSessionInput,
	saveChromeOsRequireManagedGuestSession,
} from "../components/settings/chromeos-require-managed-guest-session";
import { DefaultProfileInput, saveDefaultProfile } from "../components/settings/default-profile";
import { RequireMFAInput, saveRequireMFA } from "../components/settings/enforce-mfa";
import {
	ApprovedProfilesInput,
	ProfileEnforceEdits,
	saveProfileEnforce,
} from "../components/settings/enforced-profiles";
import { AnomalyType, FlagLevelConfig } from "../components/settings/flag-level-config";
import { FlaggedKeywordsTableProvisioned } from "../components/settings/flagged-keywords";
import { AgreementFileInput, saveAgreementFile } from "../components/settings/institute-agreement-input";
import {
	InstituteDisabledProctoringOptionsInput,
	saveInstituteDisabledProctoringOptions,
} from "../components/settings/institute-disabled-options";
import {
	GlobalProfilesInput,
	ProfilesEdits,
	saveGlobalProfiles,
} from "../components/settings/institute-global-profiles";
import InstituteMethodScopeSettings, {
	getInstituteMethodScopeSettings,
	InstituteMethodScopeSetting,
	saveInstituteMethodScopeSettings,
} from "../components/settings/institute-method-scope-settings";
import { InstituteScopeSettings, saveScopeSettings } from "../components/settings/institute-scope-settings";
import {
	InstitutionAccommodationInput,
	InstitutionAccommodationProps,
} from "../components/settings/institution-accommodations";
import { InstitutionReportEmailInput } from "../components/settings/institution-report-email";
import IntegrationSection from "../components/settings/integration";
import IntegrationSvg from "../components/settings/integration/assets/Frame.svg?react";
import { Language, LanguageSetting } from "../components/settings/language-setting";
import {
	ManagedGuestSessionRequireIncognitoInput,
	saveManagedGuestSessionRequireIncognito,
} from "../components/settings/managed-guest-session-require-incognito";
import {
	RecordingMaxAgeMonthsInput,
	RecordingMaxAgeMonthsLoadData,
} from "../components/settings/recording-max-age-months";
import SavedGeofenceInput, {
	saveNewGeofenceLocations,
	validateSavedGeofence,
} from "../components/settings/saved-geofences";
import { saveSuffix, SuffixInput } from "../components/settings/suffix";
import useFetchProctoringData from "../components/settings/useFetchProctoringData";
import { UserPinSetting } from "../components/settings/user-pin";
import PortalAuthenticationManagerContext, { PortalControllerContext } from "../context";

export type ExtraSettingsInfo = {
	"Info-instituteSettings": Awaited<ReturnType<typeof apiClient.institute.getVeritySettings>>;
	"Info-instituteProfiles": ProctoringOptionProfileV2[];
	"Info-instituteDisabledProctoringOptions": Set<keyof ProctoringOption>;
	"Info-instituteAllowStrictMode": { allowStrictMode: boolean; allowAiStrictMode: boolean };
	"Info-optionRelatedInstituteSettings": OptionRelatedInstituteSettings | undefined;
};

type VeritySettingsPageProps = {
	settingGroup: "course" | "institute" | "thirdParty" | "scope" | "user";
	hiddenH1?: string;
};

export const VeritySettingsPage = (props: VeritySettingsPageProps) => {
	const { portalContext, instituteSettings, verityInstituteSettings, courseSettings, userPrivileges } =
		useContext(PortalControllerContext);
	const { lms, instituteId, instituteUuid, lmsProvidedCourseId } = portalContext;

	const portalAuthenticationManager = useContext(PortalAuthenticationManagerContext);
	const verityFetch = (input: RequestInfo | URL, init?: RequestInit | undefined) =>
		portalAuthenticationManager?.fetch ? portalAuthenticationManager.fetch(input, init) : fetch(input, init);

	const { applications, extensions } = useFetchProctoringData();

	const emailSettingsCourse: VeritySetting = {
		settingName: "CourseEmailSetting-courseEmailSetting",
		settingTitle: i18n.t("portal.webpages.settingsRootTSX.settingsCourseName"),
		settingDescription: i18n.t("portal.webpages.settingsRootTSX.settingsCourseDescription"),
		type: "boolean",
		icon: <BellSvg fill="#42296e" />,
	};

	const flaggedKeywordsInstitute: VeritySetting<FlaggedKeyword[], FlaggedKeyword[]> = {
		settingName: "InstituteCustomSetting-flaggedKeywords",
		settingTitle: i18n.t("portal.webpages.settingsRootTSX.audioTranscriptKeywords.title"),
		type: "custom",
		icon: PersonSpeakingSvg,
		customSetting: {
			component: ({ onSettingChange }) => (
				<FlaggedKeywordsTableProvisioned onSettingChange={onSettingChange} instituteUuid={instituteUuid} />
			),
			saveFunc: async (keywords) => {
				if (!keywords || !instituteUuid) return { ok: true };
				await apiClient.institute.updateSettings(instituteUuid, {
					flaggedKeywords: keywords,
				});
				return { ok: true };
			},
			validator: (obj): obj is FlaggedKeyword[] => {
				return (
					Array.isArray(obj) &&
					obj.every(
						(item) =>
							typeof item === "object" && item !== null && "keyword" in item && typeof item.keyword === "string",
					)
				);
			},
			allowNullOrUndefinedValue: true,
		},
	};
	const emailSettingsInstitute: VeritySetting = {
		settingName: "InstituteEmailSetting-instituteEmailSetting",
		settingTitle: i18n.t("portal.webpages.settingsRootTSX.settingsInstituteName"),
		settingDescription: i18n.t("portal.webpages.settingsRootTSX.settingsInstituteDescription"),
		type: "checkbox",
		icon: <BellSvg fill="#42296e" />,
		inputText: [i18n.t("portal.webpages.settingsRootTSX.disableAll")],
		inputInfoDescription: (info) => {
			const count = Number(info?.["Info-instituteEmailCount"]);
			return Number.isNaN(count) ? null : i18n.t("portal.webpages.settingsRootTSX.digest.users", { count });
		},
	};
	const agreementFileInstitute: VeritySetting<File, { fileName?: string | null } | null> = {
		settingName: "InstituteCustomSetting-policyStatement",
		settingTitle: i18n.t("portal.webpages.settingsRootTSX.fileInstituteName"),
		settingDescription: i18n.t("portal.webpages.settingsRootTSX.fileInstituteDescription"),
		type: "custom",
		icon: PenNibSvg,
		customSetting: {
			component: (props) => AgreementFileInput({ ...props, instituteId: instituteId }),
			loadFunc: async () => {
				try {
					const response = await verityFetch(`/api/verity/agreement/${instituteId}/file-name`);

					if (!response.ok) {
						// Optional: log or handle status codes here
						console.error("Failed to fetch file name:", response.status);
						return null;
					}

					const data = (await response.json()) as {
						fileName?: string | null;
					};

					return data;
				} catch (error) {
					console.error("Error loading file name:", error);
					return null;
				}
			},
			saveFunc: async (file?: File | null, options?: { delete?: boolean }) =>
				saveAgreementFile(file, instituteId, options),
			validator: (obj: unknown): obj is File => obj instanceof File,
			allowNullOrUndefinedValue: true,
		},
	};
	const globalProfilesInstitute: VeritySetting<ProfilesEdits, undefined> = useMemo(
		() => ({
			settingName: "InstituteCustomSetting-globalProfiles",
			settingTitle: i18n.t("portal.webpages.settingsRootTSX.profileInstituteName"),
			settingDescription: i18n.t("portal.webpages.settingsRootTSX.profileInstituteDescription"),
			type: "custom",
			icon: BankSvg,
			customSetting: {
				component: (props) => <GlobalProfilesInput {...props} applications={applications} extensions={extensions} />,
				saveFunc: saveGlobalProfiles,
				validator: (obj: unknown): obj is ProfilesEdits =>
					typeof obj === "object" &&
					obj !== null &&
					"edited" in obj &&
					apiClient.proctoringProfile.schemaValidators.profiles.validate(obj.edited),
				allowNullOrUndefinedValue: true,
			},
		}),
		[applications, extensions],
	);

	const approvedProfilesInstitute: VeritySetting<ProfileEnforceEdits, undefined> = useMemo(
		() => ({
			settingName: "InstituteCustomSetting-approvedProfiles",
			settingTitle: i18n.t("portal.webpages.settingsRootTSX.approvedProfilesName"),
			settingDescription: i18n.t("portal.webpages.settingsRootTSX.approvedProfilesDescription"),
			type: "custom",
			icon: BankSvg,
			customSetting: {
				component: (props) => <ApprovedProfilesInput {...props} applications={applications} extensions={extensions} />,
				saveFunc: saveProfileEnforce,
				validator: (obj: unknown): obj is ProfileEnforceEdits =>
					typeof obj === "object" &&
					obj !== null &&
					"enforceList" in obj &&
					"unenforceList" in obj &&
					obj.enforceList instanceof Set &&
					obj.unenforceList instanceof Set,
				allowNullOrUndefinedValue: true,
			},
		}),
		[applications, extensions],
	);
	const defaultProfileInstitute: VeritySetting<{ id: number | null }, { id: number | null }> = useMemo(
		() => ({
			settingName: "InstituteCustomSetting-defaultProfile",
			settingTitle: i18n.t("portal.webpages.settingsRootTSX.defaultProfileName"),
			settingDescription: i18n.t("portal.webpages.settingsRootTSX.defaultProfileDescription"),
			type: "custom",
			icon: BankSvg,
			customSetting: {
				component: (props) => <DefaultProfileInput {...props} applications={applications} extensions={extensions} />,
				loadFunc: async () => ({ id: (await apiClient.proctoringProfile.getDefault())?.id ?? null }),
				saveFunc: saveDefaultProfile,
				validator: (obj: unknown): obj is { id: number | null } =>
					typeof obj === "object" && obj !== null && "id" in obj && (obj.id === null || typeof obj.id === "number"),
				allowNullOrUndefinedValue: true,
			},
		}),
		[applications, extensions],
	);

	const disabledProctoringOptionsInstitute: VeritySetting<
		{ instituteDisabledProctoringOptions: Set<keyof ProctoringOption> },
		{ verityInstituteSettings: VerityInstituteSettings; instituteDisabledProctoringOptions: string[] }
	> = {
		settingName: "InstituteCustomSetting-disabledProctoringOptions",
		settingTitle: i18n.t("portal.webpages.settingsRootTSX.instAssessmentRestrictionName"),
		settingDescription: i18n.t("portal.webpages.settingsRootTSX.instAssessmentRestrictionDesc"),
		type: "custom",
		icon: SealQuestionSvg,
		customSetting: {
			component: InstituteDisabledProctoringOptionsInput,
			loadFunc: async () => ({
				verityInstituteSettings: await apiClient.institute.getVeritySettings(),
				instituteDisabledProctoringOptions: await apiClient.institute.getInstituteDisabledProctoringOptions(),
			}),
			saveFunc: saveInstituteDisabledProctoringOptions,
			validator: (obj: unknown): obj is { instituteDisabledProctoringOptions: Set<keyof ProctoringOption> } =>
				typeof obj === "object" &&
				obj !== null &&
				"instituteDisabledProctoringOptions" in obj &&
				obj.instituteDisabledProctoringOptions instanceof Set,
			allowNullOrUndefinedValue: true,
		},
	};
	const bypassModeInstitute: VeritySetting<boolean, { allowStrictMode: boolean; allowAiStrictMode: boolean }> = {
		settingName: "InstituteCustomSetting-bypassMode",
		settingTitle: i18n.t("portal.webpages.settingsRootTSX.bypassMode"),
		type: "custom",
		icon: BarricadeSvg,
		customSetting: {
			component: BypassModeInput,
			loadFunc: async () => apiClient.institute.getInstituteAllowStrictMode(),
			saveFunc: saveBypassMode,
			validator: (obj: unknown): obj is boolean => typeof obj === "boolean",
			allowNullOrUndefinedValue: true,
		},
	};
	const chromeOsRequireManagedGuestSessionInstitute: VeritySetting<
		boolean,
		{
			lockdownRequireManagedGuestSession: boolean;
		}
	> = {
		settingName: "InstituteCustomSetting-chromeOsRequireManagedGuestSession",
		settingTitle: i18n.t("portal.components.settings.chromeOsRequireManagedGuestSession.title"),
		type: "custom",
		icon: LaptopSvg,
		customSetting: {
			component: ChromeOsRequireManagedGuestSessionInput,
			loadFunc: async () => (instituteUuid ? apiClient.institute.getSettings(instituteUuid) : instituteSettings),
			saveFunc: (requireManagedGuestSession) =>
				saveChromeOsRequireManagedGuestSession(instituteUuid, requireManagedGuestSession),
			validator: (obj: unknown): obj is boolean => typeof obj === "boolean",
			allowNullOrUndefinedValue: true,
		},
	};
	const managedGuestSessionRequireIncognitoInstitute: VeritySetting<
		boolean,
		{
			managedGuestSessionRequireIncognito: boolean;
		}
	> = {
		settingName: "InstituteCustomSetting-managedGuestSessionRequireIncognito",
		settingTitle: i18n.t("portal.components.settings.managedGuestSessionRequireIncognito.title"),
		type: "custom",
		icon: IncognitoSvg,
		customSetting: {
			component: ManagedGuestSessionRequireIncognitoInput,
			loadFunc: async () => (instituteUuid ? apiClient.institute.getSettings(instituteUuid) : instituteSettings),
			saveFunc: (requireIncognito) => saveManagedGuestSessionRequireIncognito(instituteUuid, requireIncognito),
			validator: (obj: unknown): obj is boolean => typeof obj === "boolean",
			allowNullOrUndefinedValue: true,
		},
	};

	const requireMFA: VeritySetting<boolean, { mfaRequired: boolean }> = {
		settingName: "InstituteCustomSetting-requireMFA",
		settingTitle: i18n.t("portal.components.settings.requireMFA.title"),
		type: "custom",
		icon: PasswordLockSvg,
		customSetting: {
			component: RequireMFAInput,
			loadFunc: async () => {
				if (!instituteUuid) return { mfaRequired: false };

				const accessControlInfo = await apiClient.institute.getAccessControlInfo(instituteUuid);
				return { mfaRequired: Boolean(accessControlInfo.mfaRequired) };
			},
			saveFunc: (requireMFA) => saveRequireMFA(instituteUuid, requireMFA),
			validator: (obj: unknown): obj is boolean => typeof obj === "boolean",
			allowNullOrUndefinedValue: true,
		},
	};
	const allowQuizAttemptOnBrowserSettingInstitute: VeritySetting<
		AllowQuizAttemptOnBrowserSettingData,
		AllowQuizAttemptOnBrowserSettingData
	> = {
		settingName: "InstituteCustomSetting-allowQuizAttemptOnBrowser",
		settingTitle: i18n.t("portal.components.settings.allowQuizAttemptOnBrowser.title"),
		type: "custom",
		icon: IconBrowsers,
		customSetting: {
			component: AllowQuizAttemptOnBrowserSetting,
			loadFunc: async () => (instituteUuid ? apiClient.institute.getSettings(instituteUuid) : instituteSettings),
			saveFunc: async (savePayload) => {
				try {
					if (!savePayload) return { ok: true };
					if (!instituteUuid) throw new Error(`No instituiteUuid`);

					const { allowQuizAttemptOnChrome, allowQuizAttemptOnFirefox, allowQuizAttemptOnSafari } = savePayload;
					await apiClient.institute.updateSettings(instituteUuid, {
						allowQuizAttemptOnChrome,
						allowQuizAttemptOnFirefox,
						allowQuizAttemptOnSafari,
					});

					return { ok: true };
				} catch (error) {
					console.error("Error saving allowQuizAttemptOnBrowser settings:", error);
					return { ok: false, error: "Failed to save settings" };
				}
			},
			validator: (obj: unknown): obj is AllowQuizAttemptOnBrowserSettingData =>
				allowQuizAttemptOnBrowserSettingSchema.safeParse(obj).success,
			allowNullOrUndefinedValue: true,
		},
	};
	const recordingMaxAgeMonthsInstitute: VeritySetting<number, RecordingMaxAgeMonthsLoadData> = {
		settingName: "InstituteCustomSetting-recordingMaxAgeMonths",
		settingTitle: i18n.t("portal.webpages.settingsRootTSX.recordingMaxAgeMonths.title"),
		type: "custom",
		icon: RecordingMaxAgeIcon,
		customSetting: {
			component: RecordingMaxAgeMonthsInput,
			loadFunc: async () =>
				instituteUuid
					? apiClient.trpc.trpc.institute.getSettings.query({ instituteUuid }).then((data) => data.instituteSettings)
					: instituteSettings,
			saveFunc: async (months) => {
				try {
					if (!months) return { ok: true };
					if (!instituteUuid) throw new Error(`No instituiteUuid`);
					await apiClient.trpc.trpc.institute.updateSettings.mutate({
						instituteUuid,
						settings: { recordingMaxAgeMonths: months },
					});
					return { ok: true };
				} catch (error) {
					console.error("Error saving max recording age setting:", error);
					return { ok: false, reason: "Failed to save max recording age setting." };
				}
			},
			validator: (obj: unknown): obj is number =>
				typeof obj === "number" && Number.isInteger(obj) && obj >= 1 && obj <= 12,
			allowNullOrUndefinedValue: true,
		},
	};
	const methodScopesInstitute: VeritySetting<InstituteMethodScopeSetting, InstituteMethodScopeSetting> = {
		settingName: "InstituteCustomSetting-methodScopes",
		settingTitle: i18n.t("portal.components.settings.instituteMethodScopeSettings.title"),
		type: "custom",
		icon: MethodScopeSettingSvg,
		customSetting: {
			component: (props) => <InstituteMethodScopeSettings {...props} />,
			loadFunc: async () => getInstituteMethodScopeSettings(instituteUuid),
			saveFunc: (data) => saveInstituteMethodScopeSettings(instituteUuid, data),
			validator: (obj: unknown): obj is InstituteMethodScopeSetting => typeof obj === "object" && !!obj,
			allowNullOrUndefinedValue: true,
		},
	};
	const suffixInstitute: VeritySetting<string | null, { suffix: string | null }> = {
		settingName: "InstituteCustomSetting-suffix",
		settingTitle: i18n.t("portal.webpages.settingsRootTSX.suffix"),
		type: "custom",
		icon: SuffixSvg,
		customSetting: {
			component: (inputProps) => SuffixInput({ ...inputProps, instituteId, lms }),
			loadFunc: async () => ({ suffix: await apiClient.institute.getSuffix() }),
			saveFunc: saveSuffix,
			validator: (obj: unknown): obj is string | null => obj === null || typeof obj === "string",
			allowNullOrUndefinedValue: true,
		},
	};

	const thirdPartyIntegrationInstitute: VeritySetting<ThirdPartyPlatformRosters, ThirdPartyPlatformRosters> = {
		settingName: "InstituteCustomSetting-integration",
		settingTitle: i18n.t("portal.webpages.settingsRootTSX.thirdPartyIntegration.title"),
		type: "custom",
		icon: IntegrationSvg,
		customSetting: {
			component: IntegrationSection,
			loadFunc: async () => await apiClient.institute.getInstituteThirdPartyPlatforms(),
			saveFunc: async (data) => {
				const previousPlatforms = await apiClient.institute.getInstituteThirdPartyPlatforms();
				const previousPlatformIds = new Set(
					Object.values(previousPlatforms.selectedPlatformRoster).map((p) => p.platformId),
				);
				const filteredSelectedPlatforms: ThirdPartyPlatform[] | undefined = data
					? Object.values(
							Object.fromEntries(
								Object.entries(data.selectedPlatformRoster).filter(
									([_, platform]) => !previousPlatformIds.has(platform.platformId),
								),
							),
						)
					: undefined;

				return apiClient.institute.saveInstituteThirdPartyPlatforms(data, filteredSelectedPlatforms);
			},
			validator: (obj: unknown): obj is ThirdPartyPlatformRosters =>
				typeof obj === "object" && obj !== null && "selectedPlatformRoster" in obj && "unselectedPlatformRoster" in obj,
			allowNullOrUndefinedValue: true,
		},
	};

	const courseFlagLevelConfig: VeritySetting<Record<AnomalyType, number>, Record<AnomalyType, number> | null> = {
		settingName: "CourseCustomSetting-flagLevelConfig",
		settingTitle: i18n.t("portal.webpages.settingsRootTSX.courseFlagName"),
		settingDescription: i18n.t("portal.webpages.settingsRootTSX.courseFlagDescription"),
		type: "custom",
		icon: <FlagSvg fill="#42296e" />,
		customSetting: {
			component: (props) => <FlagLevelConfig {...props} />,
			loadFunc: async () =>
				(await (
					await verityFetch(`/api/verity/anomaly-config/${instituteId}/${lmsProvidedCourseId}`)
				).json()) as Record<AnomalyType, number> | null,
			saveFunc: async (setting) => {
				if (setting) {
					const resp = await verityFetch(`/api/verity/anomaly-config/${instituteId}/${lmsProvidedCourseId}`, {
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({
							data: setting,
						}),
					});
					return resp;
				}
				return { ok: true };
			},
			validator: (obj: unknown): obj is Record<AnomalyType, number> => obj instanceof Object,
			allowNullOrUndefinedValue: true,
		},
	};

	const savedGeofencesCourse: VeritySetting<GeofenceLocation[], GeofenceLocation[]> = {
		settingName: "CourseCustomSetting-savedGeofences",
		settingTitle: i18n.t("portal.webpages.settingsRootTSX.savedGeofencesCourse"),
		type: "custom",
		icon: <SavedGeofence fill="#42296e" />,
		customSetting: {
			component: SavedGeofenceInput,
			loadFunc: async () =>
				portalContext.courseId
					? (await apiClient.course.getCourseSettings(portalContext.courseId)).geofenceLocations
					: courseSettings.geofenceLocations,
			saveFunc: async (geofenceLocations) => saveNewGeofenceLocations(portalContext.courseId, geofenceLocations),
			validator: validateSavedGeofence,
			allowNullOrUndefinedValue: true,
		},
	};

	const scopeInstitute: VeritySetting<
		{ scopeSettingsData: ScopeSettingsInfo },
		{ scopeSettingsData: ScopeSettingsInfo }
	> = {
		settingName: "InstituteCustomSetting-scope",
		settingTitle: i18n.t("portal.webpages.settingsRootTSX.scopeSettingsName"),
		type: "custom",
		icon: ScopeSettingSvg,
		customSetting: {
			component: (p) => (lms ? InstituteScopeSettings({ ...p, lmsType: lms }) : null),
			loadFunc: async () => ({
				scopeSettingsData: await apiClient.institute.getInstituteScopeSettings(),
			}),
			saveFunc: saveScopeSettings,
			validator: (obj: unknown): obj is { scopeSettingsData: ScopeSettingsInfo } => typeof obj === "object",
			allowNullOrUndefinedValue: true,
		},
	};

	const userPinSetting: VeritySetting = {
		settingName: "UserCustomSetting-pin",
		settingTitle: i18n.t("portal.webpages.settingsRootTSX.userSecurityPinName"),
		settingDescription: i18n.t("portal.webpages.settingsRootTSX.userSecurityPinDescription"),
		type: "custom",
		icon: ShieldSvg,
		customSetting: {
			component: () => <UserPinSetting verityFetch={verityFetch} />,
			//functions do not need to be implemented because the setting cannot be modified or saved
			saveFunc: async () => {
				return { ok: true };
			},
			validator: function (_obj: unknown): _obj is void {
				return true;
			},
			allowNullOrUndefinedValue: true,
		},
	};

	const languageSetting: VeritySetting<Language, Language> = {
		settingName: "languageSetting-pin",
		settingTitle: i18n.t("portal.webpages.settingsRootTSX.languageSettingsName"),
		settingDescription: i18n.t("portal.webpages.settingsRootTSX.languageSettingsDescription"),
		type: "custom",
		icon: ScopeSettingSvg,
		customSetting: {
			component: LanguageSetting,
			loadFunc: async () =>
				(await apiClient.user.getUserLanguage(await apiClient.user.getUserId())) ||
				((await apiClient.institute.getLanguage()) as Language),
			saveFunc: async (data) => {
				try {
					if (data) {
						await apiClient.user.saveUserLanguage(await apiClient.user.getUserId(), data);
						await i18n.changeLanguage(data);
						await packageLanguageChanger(data);
						if (window.chrome) {
							chrome?.runtime?.sendMessage?.(
								getInferredBrowserExtensionId(),
								{
									request: "changeLanguage",
									language: data,
								},
								() => {
									if (chrome?.runtime?.lastError) {
										console.error(`'Error changing language in extension': ${chrome.runtime.lastError.message}`);
									}
								},
							);
						} else {
							dispatchProxyMessage({
								request: "changeLanguage",
								language: data,
							}).catch((error) => {
								console.error("Error changing language:", error);
							});
						}
						window.top?.postMessage(
							{
								subject: "__LANGUAGE_CHANGE",
								payload: { language: data },
							},
							"*",
						);
						return { ok: true };
					}
					return { ok: false, reason: "Failed to save user language." };
				} catch (err) {
					console.error(err);
					return { ok: false, reason: "Failed to save user language." };
				}
			},
			validator: (obj: unknown): obj is Language => typeof obj === "string",
			allowNullOrUndefinedValue: true,
		},
	};

	const institutionAccommodationSetting: VeritySetting<Map<number, ExemptedStudent>, InstitutionAccommodationProps> = {
		settingName: "InstituteCustomSetting-institutionAccommodation",
		settingTitle: i18n.t("portal.webpages.settingsRootTSX.institutionAccommodationName"),
		type: "custom",
		icon: ExemptionSvg,
		customSetting: {
			component: InstitutionAccommodationInput,
			loadFunc: async () => {
				const verityInstituteSettings = await apiClient.institute.getVeritySettings();
				const optionRelatedInstituteSettings = instituteUuid
					? await apiClient.institute.getSettings(instituteUuid)
					: instituteSettings;
				const studentRoster = await apiClient.user.getInstituteStudents();
				const accommodationRoster = await apiClient.institute.getInstitutionProctoringAccommodation();
				const profiles = await apiClient.proctoringProfile.getAll({});
				const profilesMap = new Map(
					profiles
						.filter(
							(profile) =>
								(profile.proctoringOption.isExtension && verityInstituteSettings.allowExtensionPlatform) ||
								(profile.proctoringOption.isLockdown && verityInstituteSettings.allowLockdownPlatform) ||
								(profile.proctoringOption.isMobile && verityInstituteSettings.allowMobilePlatform),
						)
						.map((x) => [x.id, x]),
				);

				const nonExemptRoster = new Map<number, ExemptedStudent>();
				const exemptRoster = new Map<number, ExemptedStudent>();
				studentRoster.forEach((student) => {
					const exemptedStudent = accommodationRoster.find((exemptedStudent) => exemptedStudent.userId === student.id);
					if (exemptedStudent) {
						exemptRoster.set(student.id, {
							userId: student.id,
							name: student.name,
							accommodationNotes: exemptedStudent.accommodationNotes ?? undefined,
							proctoringOptionProfileId: exemptedStudent.proctoringOptionProfileId ?? undefined,
						});
					} else {
						nonExemptRoster.set(student.id, {
							userId: student.id,
							name: student.name,
						});
					}
				});
				const exemptionInfo = {
					nonExemptRoster,
					exemptRoster,
				};

				return {
					profilesMap,
					optionRelatedInstituteSettings,
					verityInstituteSettings,
					exemptionInfo,
					applications,
					extensions,
				};
			},
			saveFunc: async (data) => {
				if (data) {
					await apiClient.institute.updateInstitutionProctoringAccommodation(
						[...data.values()].map((student) => ({
							userId: student.userId,
							accommodationNotes: student.accommodationNotes,
							proctoringOptionProfileId: student.proctoringOptionProfileId,
						})),
					);
					return { ok: true };
				}
				return { ok: false, reason: "Invalid data type provided." };
			},
			validator: (obj: unknown): obj is Map<number, ExemptedStudent> =>
				obj instanceof Map &&
				[...obj.values()].every((val) => val && typeof val === "object" && "userId" in val && "name" in val),
			allowNullOrUndefinedValue: true,
		},
	};

	const institutionReportEmailSetting: VeritySetting<boolean, { enabled: boolean }> = {
		settingName: "UserCustomSetting-institutionReportEmail",
		settingTitle: i18n.t("portal.webpages.settingsRootTSX.settingsInstitutionEmailName"),
		type: "custom",
		icon: BellSvg,
		customSetting: {
			component: InstitutionReportEmailInput,
			loadFunc: async () => {
				return { enabled: await apiClient.report.getReceiveInstitutionReportEmail() };
			},
			saveFunc: async (data) => {
				if (typeof data === "boolean") {
					await apiClient.report.updateReceiveInstitutionReportEmail(data);
					return { ok: true };
				}
				return { ok: false, reason: "Invalid data type provided." };
			},
			validator: (obj: unknown): obj is boolean => typeof obj === "boolean",
			allowNullOrUndefinedValue: true,
		},
	};

	let settingGroup: VeritySettingGroup = [];

	if (props.settingGroup === "scope") {
		settingGroup = [scopeInstitute];
	} else if (props.settingGroup === "institute") {
		settingGroup = [];
		if (!verityInstituteSettings.liteMode) {
			settingGroup = settingGroup.concat([globalProfilesInstitute, approvedProfilesInstitute, defaultProfileInstitute]);
		}
		settingGroup.push(disabledProctoringOptionsInstitute);
		if (!verityInstituteSettings.liteMode) {
			settingGroup = settingGroup.concat([
				institutionAccommodationSetting,
				flaggedKeywordsInstitute,
				bypassModeInstitute,
			]);
		}
		if (verityInstituteSettings.allowLockdownPlatform) {
			settingGroup.push(chromeOsRequireManagedGuestSessionInstitute);
		}
		settingGroup.push(requireMFA);
		settingGroup.push(managedGuestSessionRequireIncognitoInstitute);
		settingGroup.push(recordingMaxAgeMonthsInstitute);
		settingGroup.push(allowQuizAttemptOnBrowserSettingInstitute);
		if (!verityInstituteSettings.liteMode && lms) {
			settingGroup.push(methodScopesInstitute);
		}
		if (lms === "canvas") {
			settingGroup.push(suffixInstitute);
		}
		if (!verityInstituteSettings.liteMode) {
			settingGroup.push(emailSettingsInstitute);
		}
		settingGroup.push(agreementFileInstitute);
	} else if (props.settingGroup === "thirdParty") {
		settingGroup = [thirdPartyIntegrationInstitute];
	} else if (props.settingGroup === "user") {
		settingGroup = [
			userPinSetting,
			languageSetting,
			...(canAccessResource(
				{ privileges: userPrivileges },
				{
					privilege: "INSTITUTE_REPORT",
					action: "READ",
					resourceScope: { instituteId: instituteUuid, resourceId: instituteUuid },
				},
			) && !verityInstituteSettings.liteMode
				? [institutionReportEmailSetting]
				: []),
		];
	} else if (props.settingGroup === "course") {
		if (getProduct() !== "candidate") settingGroup.push(emailSettingsCourse);

		if (!(instituteSettings.disabledProctoringOptions as string[] | null)?.includes("enableGeofence")) {
			settingGroup.push(savedGeofencesCourse);
		}
		settingGroup.push(courseFlagLevelConfig);
	}

	const pageProps: SettingsPageProps = {
		settingGroup: settingGroup,
		settingGroupName: props.settingGroup,
		hiddenH1: props.hiddenH1,
		loadFunc: async (
			settingsLoader: (_data: Record<string, string | unknown>) => void,
			customSettings?: Record<string, CustomSetting<unknown, unknown>>,
		): Promise<boolean> => {
			return await loadSettingsOnLoad(settingsLoader, customSettings);
		},
		authFunc: async () => {
			return (await fetchAuth()).length > 0;
		},
		saveFunc: async (
			data: Record<string, string | CustomSettingData>,
			customSettings?: Record<string, CustomSetting<unknown, unknown>>,
		): Promise<SaveResponse> => {
			return await saveSettings(data, customSettings);
		},
	};

	return (
		<ThemeWrapper scoped={false}>
			<SettingsPage {...pageProps} />
		</ThemeWrapper>
	);

	async function loadSettingsOnLoad(
		settingsLoader: (_data: Record<string, string | unknown>) => void,
		customSettings: Record<string, CustomSetting<unknown, unknown>> = {},
	): Promise<boolean> {
		try {
			const getSettings = async () => {
				return (await (
					await verityFetch(`/api/verity/portal/settings?lmsCourseId=${lmsProvidedCourseId ?? "null"}`)
				).json()) as Record<string, string>;
			};
			const getCustomSettings = async () => {
				const promiseList = [];
				for (const [name, custom] of Object.entries(customSettings)) {
					promiseList.push((async () => ({ [name]: custom.loadFunc ? await custom.loadFunc() : true }))());
				}
				const respList = await Promise.all(promiseList);
				const customSettingsResp: Record<string, unknown> = respList.reduce((obj, v) => ({ ...obj, ...v }), {});
				return customSettingsResp;
			};
			const getExtraSettingsInfo = async (): Promise<ExtraSettingsInfo> => {
				const [
					infoInstituteSettings,
					instituteProfiles,
					instituteDisabledProctoringOptions,
					instituteAllowStrictMode,
					optionRelatedInstituteSettings,
				] = await Promise.all([
					apiClient.institute.getVeritySettings(),
					apiClient.proctoringProfile.getAll({}).then((x) => x.filter((p) => isInstituteProfile(p))),
					apiClient.institute.getInstituteDisabledProctoringOptions().then((x) => new Set(x)),
					apiClient.institute.getInstituteAllowStrictMode(),
					instituteUuid ? apiClient.institute.getSettings(instituteUuid) : instituteSettings,
				]);
				return {
					"Info-instituteSettings": infoInstituteSettings,
					"Info-instituteProfiles": instituteProfiles,
					"Info-instituteDisabledProctoringOptions": instituteDisabledProctoringOptions,
					"Info-instituteAllowStrictMode": instituteAllowStrictMode,
					"Info-optionRelatedInstituteSettings": optionRelatedInstituteSettings,
				};
			};

			const [settingsResp, customSettingsResp, extraSettingsResp] = await Promise.all([
				getSettings(),
				getCustomSettings(),
				getExtraSettingsInfo(),
			]);
			settingsLoader({ ...settingsResp, ...customSettingsResp, ...extraSettingsResp });
			return true;
		} catch (error) {
			console.error(`Failed to load YuJa Verity settings. `, error);
			return false;
		}
	}

	async function fetchAuth(): Promise<string[]> {
		try {
			const userPerms = await apiClient.permission.portalPermissions(lmsProvidedCourseId);
			return userPerms;
		} catch (error) {
			console.error(`Failed to get user permissions. `, error);
		}
		return [];
	}

	async function saveSettings(
		data: Record<string, string | CustomSettingData>,
		customSettings: Record<string, CustomSetting<unknown, unknown>> = {},
	): Promise<SaveResponse> {
		let errorReason: string | undefined;

		const validateNullOrUndefined = (obj: unknown): obj is null | undefined => obj === null || obj === undefined;

		const saveSettings = async () => {
			const payload: Record<string, unknown> = {};
			for (const [prop, value] of Object.entries(data)) {
				if (typeof value === "string") payload[prop] = JSON.parse(value);
			}
			if (
				!(
					await verityFetch(`/api/verity/portal/settings?lmsCourseId=${lmsProvidedCourseId ?? "null"}`, {
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify(payload),
					})
				).ok
			) {
				throw new Error();
			}
		};
		const saveCustomSettings = async () => {
			const promiseList = [];
			for (const [name, custom] of Object.entries(customSettings)) {
				const customPayload = data[name];
				if (typeof customPayload !== "string" && customPayload) {
					if (
						custom.validator(customPayload.value) ||
						(custom.allowNullOrUndefinedValue && validateNullOrUndefined(customPayload.value))
					) {
						promiseList.push(custom.saveFunc(customPayload.value, customPayload.options));
					} else {
						throw new Error(`${name}: Incorrect setting value type.`);
					}
				}
			}
			const respList = await Promise.all(promiseList);
			for (const resp of respList) {
				if (!resp.ok) {
					errorReason = resp.reason;
					throw new Error();
				}
			}
		};

		try {
			await apiClient.authentication.isAuthenticated();
			await Promise.all([saveSettings(), saveCustomSettings()]);
			return { ok: true };
		} catch (error) {
			console.error(`Failed to save some of the settings. `, error);
		}
		return { ok: false, reason: errorReason };
	}
};
